import {
  ArrowLeft,
  Users,
  Package,
  Wrench,
  Check,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import AddressFields from "../components/shared/AddressFields";
import GCSelector from "../components/shared/GCSelector";
import CompanyPicker from "../components/contacts/CompanyPicker";
import { useCreateJob } from "../hooks/useCreateJob";
import { useParams } from "react-router-dom";
import JobContactsEditor from "../components/job/JobContactsEditor";


const CreateJobPage = () => {
  const { jobId } = useParams();
  const {
    loading, generalContractors, form, isEdit, isContracted,
    laborEstimate, materialEstimate, equipmentEstimate,
    totalLaborEstimate, totalMaterialEstimate, totalEquipmentEstimate,
    contractAmount, sortedGCs, saveStatus, defaultContactCompany,
    setForm, setLaborEstimate, setMaterialEstimate, setEquipmentEstimate,
    addContact,
    updateContactEntry,
    removeContact,
    handleSubmit, handleGcChange, handleBiddingGcChange,
    navigate, formatCurrency,
  } = useCreateJob();

  const isBidding = form.status === "bidding" || form.status === "bid_complete";

  return (
    <div className="page-enter space-y-8" data-testid="create-job-page">
      {/* Header */}
      <div className="page-header flex items-center justify-between">
        <div>
          <Button
            variant="outline"
            className="fixed top-3 left-16 md:top-4 md:left-[calc(var(--sidebar-w,16rem)+1.5rem)] z-30 h-9 bg-background/90 backdrop-blur shadow-sm"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {isEdit ? "Back to Job" : "Back to Jobs"}
          </Button>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">{isEdit ? "Edit Job" : "Create New Job"}</h1>
        </div>
        {isEdit && (
          <div className="flex items-center gap-2 text-sm">
            {saveStatus === "saving" && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />Saving...
              </span>
            )}
            {saveStatus === "saved" && (
              <span className="flex items-center gap-1.5 text-green-600">
                <Check className="w-3.5 h-3.5" />Saved
              </span>
            )}
            {saveStatus === "unsaved" && (
              <span className="text-amber-600">Unsaved changes</span>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" autoComplete="off">
        {/* Card 1: Job Information */}
        <Card>
          <CardHeader><CardTitle>Job Information</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="form-group">
              <Label htmlFor="name">Job Name *</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ name: e.target.value })} autoComplete="off" data-lpignore="true" required placeholder="e.g., Smith Residence Patio" data-testid="job-name-input" />
            </div>
            <div className="form-group">
              <Label htmlFor="client">General Contractor{isBidding ? "s" : ""} *</Label>
              {generalContractors.length > 0 ? (
                isBidding ? (
                  <GCSelector
                    generalContractors={generalContractors}
                    selectedGcIds={form.bidding_gc_ids}
                    onSelectionChange={handleBiddingGcChange}
                    label=""
                  />
                ) : (
                  <Select value={form.general_contractor_id} onValueChange={handleGcChange}>
                    <SelectTrigger data-testid="gc-select"><SelectValue placeholder="Select a General Contractor" /></SelectTrigger>
                    <SelectContent>
                      {sortedGCs.map((gc) => (
                        <SelectItem key={gc.id} value={gc.id}>{gc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              ) : (
                <CompanyPicker id="client" value={form.client_name} onChange={(v) => setForm({ client_name: v })} placeholder="e.g., ABC Construction" testId="client-name-input" />
              )}
              {generalContractors.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">Tip: Add general contractors in Settings for easy selection</p>
              )}
            </div>
            <div className="form-group">
              <Label htmlFor="contract_amount">Contract Amount ($)</Label>
              <NumericInput id="contract_amount" decimals={2} min={0} value={form.contract_amount} onChange={(e) => setForm({ contract_amount: e.target.value })} placeholder="e.g., 50000" data-testid="contract-amount-input" />
            </div>
            <div className="form-group">
              <Label htmlFor="contract_number" className={!isContracted ? "text-muted-foreground" : ""}>Contract #</Label>
              <Input
                id="contract_number"
                autoComplete="off"
                value={form.contract_number}
                onChange={(e) => setForm({ contract_number: e.target.value })}
                placeholder={!isContracted ? "Set when contracted" : "e.g., CNT-2026-001"}
                disabled={!isContracted}
                className={!isContracted ? "opacity-50" : ""}
                data-testid="contract-number-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="billing_day" className={!isContracted ? "text-muted-foreground" : ""}>Billing Day of Month</Label>
              <Select value={form.billing_date} onValueChange={(v) => setForm({ billing_date: v })} disabled={!isContracted}>
                <SelectTrigger data-testid="billing-day-select" className={!isContracted ? "opacity-50" : ""}>
                  <SelectValue placeholder={!isContracted ? "Set when contracted" : "Select day..."} />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(31)].map((_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} of each month
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="form-group">
              <Label htmlFor="project_number" className={!isContracted ? "text-muted-foreground" : ""}>Project #</Label>
              <Input
                id="project_number"
                autoComplete="off"
                value={form.project_number}
                onChange={(e) => setForm({ project_number: e.target.value })}
                placeholder={!isContracted ? "Set when contracted" : "e.g., P-2026-001"}
                disabled={!isContracted}
                className={!isContracted ? "opacity-50" : ""}
                data-testid="project-number-input"
              />
            </div>
            {isBidding && (
              <div className="grid grid-cols-2 gap-4 md:col-span-2">
                <div className="form-group">
                  <Label htmlFor="bid_due_date">Bid Due Date</Label>
                  <Input id="bid_due_date" type="date" value={form.bid_due_date} onChange={(e) => setForm({ bid_due_date: e.target.value })} data-testid="bid-due-date-input" />
                </div>
                <div className="form-group">
                  <Label htmlFor="bid_due_time">Time Due</Label>
                  <Input id="bid_due_time" type="time" value={form.bid_due_time || ""} onChange={(e) => setForm({ bid_due_time: e.target.value })} data-testid="bid-due-time-input" />
                </div>
              </div>
            )}
            <div className="form-group">
              <Label htmlFor="start_date">Start Date</Label>
              <Input id="start_date" type="date" value={form.start_date} onChange={(e) => setForm({ start_date: e.target.value })} data-testid="start-date-input" />
            </div>
            <div className="form-group">
              <Label htmlFor="end_date">End Date</Label>
              <Input id="end_date" type="date" value={form.end_date} onChange={(e) => setForm({ end_date: e.target.value })} data-testid="end-date-input" />
            </div>
            <div className="form-group md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" value={form.description} onChange={(e) => setForm({ description: e.target.value })} placeholder="Job description and notes..." rows={3} data-testid="description-input" />
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Contact Information */}
        <Card>
          <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
          <CardContent>
            <JobContactsEditor contacts={form.contact_list || []} onAdd={addContact} onUpdate={updateContactEntry} onRemove={removeContact} defaultCompany={defaultContactCompany} />
          </CardContent>
        </Card>

        {/* Card 3: Job Site Address */}
        <Card>
          <CardHeader><CardTitle>Job Site Address</CardTitle></CardHeader>
          <CardContent>
            <AddressFields
              values={{ street: form.street, apt: form.apt, city: form.city, state: form.state, zip_code: form.zip_code }}
              onChange={(field, value) => setForm({ [field]: value })}
              onChangeMany={(patch) => setForm(patch)}
              prefix="job"
              title=""
            />
          </CardContent>
        </Card>

        {/* Estimates */}
        <div className="space-y-2">
          <p className="text-red-600 font-semibold text-sm" data-testid="lump-sum-header">Use These for Lump Sum Proposals</p>
          <Tabs defaultValue="labor" className="space-y-6">
            <TabsList>
              <TabsTrigger value="labor" data-testid="labor-estimates-tab"><Users className="w-4 h-4 mr-2" />Lump Sum Labor</TabsTrigger>
              <TabsTrigger value="materials" data-testid="material-estimates-tab"><Package className="w-4 h-4 mr-2" />Lump Sum Material</TabsTrigger>
              <TabsTrigger value="equipment" data-testid="equipment-estimates-tab"><Wrench className="w-4 h-4 mr-2" />Lump Sum Equipment</TabsTrigger>
            </TabsList>

            <TabsContent value="labor">
              <Card>
                <CardHeader><CardTitle>Lump Sum Labor</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-red-600 text-sm font-medium" data-testid="labor-before-tax-note">Entered Values are BEFORE Tax, Profit and Burden</p>
                  <div className="max-w-md">
                    <div className="form-group">
                      <Label htmlFor="labor-estimate">Total Labor Cost ($)</Label>
                      <NumericInput id="labor-estimate" decimals={2} min={0} value={laborEstimate} onChange={(e) => setLaborEstimate(e.target.value)} placeholder="Enter total labor estimate" className="text-lg" data-testid="labor-estimate-input" />
                      <p className="text-sm text-muted-foreground mt-2">Enter the total estimated labor cost for this job as a lump sum.</p>
                    </div>
                  </div>
                  <div className="flex justify-end pt-4 border-t border-border">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Lump Sum Labor</p>
                      <p className="text-2xl font-bold text-foreground">{formatCurrency(totalLaborEstimate)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="materials">
              <Card>
                <CardHeader><CardTitle>Lump Sum Material</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-red-600 text-sm font-medium" data-testid="material-before-tax-note">Entered Values are BEFORE Tax, Profit and Burden</p>
                  <div className="max-w-md">
                    <div className="form-group">
                      <Label htmlFor="material-estimate">Total Material Cost ($)</Label>
                      <NumericInput id="material-estimate" decimals={2} min={0} value={materialEstimate} onChange={(e) => setMaterialEstimate(e.target.value)} placeholder="Enter total material estimate" className="text-lg" data-testid="material-estimate-input" />
                      <p className="text-sm text-muted-foreground mt-2">Enter the total estimated material cost for this job as a lump sum.</p>
                    </div>
                  </div>
                  <div className="flex justify-end pt-4 border-t border-border">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Lump Sum Material</p>
                      <p className="text-2xl font-bold text-foreground">{formatCurrency(totalMaterialEstimate)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="equipment">
              <Card>
                <CardHeader><CardTitle>Lump Sum Equipment</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-red-600 text-sm font-medium" data-testid="equipment-before-tax-note">Entered Values are BEFORE Tax, Profit and Burden</p>
                  <div className="max-w-md">
                    <div className="form-group">
                      <Label htmlFor="equipment-estimate">Total Equipment Cost ($)</Label>
                      <NumericInput id="equipment-estimate" decimals={2} min={0} value={equipmentEstimate} onChange={(e) => setEquipmentEstimate(e.target.value)} placeholder="Enter total equipment estimate" className="text-lg" data-testid="equipment-estimate-input" />
                      <p className="text-sm text-muted-foreground mt-2">Enter the total estimated equipment cost for this job as a lump sum.</p>
                    </div>
                  </div>
                  <div className="flex justify-end pt-4 border-t border-border">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Lump Sum Equipment</p>
                      <p className="text-2xl font-bold text-foreground">{formatCurrency(totalEquipmentEstimate)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Summary & Submit — floats at the bottom of the viewport while scrolling */}
        <Card className="sticky bottom-4 z-30 bg-background/95 backdrop-blur shadow-lg border-border" data-testid="job-summary-bar">
          <CardContent className="py-3 px-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-6">
                {contractAmount > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground">Contract Amount</p>
                    <p className="text-xl font-bold text-foreground leading-tight">{formatCurrency(contractAmount)}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">Total Job Estimate</p>
                  <p className="text-2xl font-bold text-foreground leading-tight">{formatCurrency(totalLaborEstimate + totalMaterialEstimate + totalEquipmentEstimate)}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => navigate(isEdit ? `/jobs/${jobId}` : "/jobs")}>Cancel</Button>
                {!isEdit && (
                  <Button type="submit" disabled={loading || !form.name || (!form.client_name && !form.general_contractor_id && (!form.bidding_gc_ids || form.bidding_gc_ids.length === 0))} data-testid="submit-job-btn">
                    {loading ? "Creating..." : "Create Job"}
                  </Button>
                )}
                {isEdit && (
                  <Button type="button" onClick={() => navigate(`/jobs/${jobId}`)} data-testid="done-editing-btn">
                    Done
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default CreateJobPage;
